import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import {
  Search,
  Plus,
  UserCog,
  Eye,
  EyeOff,
  Edit3,
  Trash2,
  X,
  Save,
  AlertTriangle,
  Shield,
  ShieldCheck,
  UserCheck,
} from 'lucide-react';
import { clsx } from 'clsx';
import toast from 'react-hot-toast';
import api from '../config/api';
import SearchableSelect from '../components/SearchableSelect';

const avatarColors = [
  'bg-emerald-100 text-emerald-700',
  'bg-blue-100 text-blue-700',
  'bg-purple-100 text-purple-700',
  'bg-amber-100 text-amber-700',
  'bg-rose-100 text-rose-700',
];

const roleColors = {
  admin: 'bg-red-100 text-red-700',
  pharmacist: 'bg-emerald-100 text-emerald-700',
  cashier: 'bg-blue-100 text-blue-700',
};

export default function Users() {
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState('');
  const [viewUser, setViewUser] = useState(null);
  const [editUser, setEditUser] = useState(null);
  const [deleteUser, setDeleteUser] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [showEditPassword, setShowEditPassword] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [showAddPassword, setShowAddPassword] = useState(false);
  const [addForm, setAddForm] = useState({ username: '', full_name: '', email: '', password: '', role: 'cashier', is_active: 1 });

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    try {
      const res = await api.get('/users?limit=500');
      const list = Array.isArray(res) ? res : (res?.users || []);
      setUsers(list);
    } catch (err) {
      toast.error(err.message || 'Failed to load users');
    }
  };

  const filtered = users.filter(
    (u) =>
      (u.full_name && u.full_name.toLowerCase().includes(search.toLowerCase())) ||
      (u.username && u.username.toLowerCase().includes(search.toLowerCase())) ||
      (u.email && u.email.toLowerCase().includes(search.toLowerCase()))
  );

  const activeCount = users.filter((u) => u.is_active).length;

  const openAdd = () => {
    setAddForm({ username: '', full_name: '', email: '', password: '', role: 'cashier', is_active: 1 });
    setShowAddPassword(false);
    setShowAdd(true);
  };

  const saveNew = async (e) => {
    e.preventDefault();
    if (!addForm.username || !addForm.full_name || !addForm.email || !addForm.password) return;
    if (addForm.password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    try {
      await api.post('/users', { username: addForm.username, full_name: addForm.full_name, email: addForm.email, role: addForm.role, password: addForm.password });
      toast.success('User added');
      setShowAdd(false);
      loadUsers();
    } catch (err) {
      toast.error(err.message || 'Failed to add user');
    }
  };

  const openEdit = (user) => {
    setEditUser(user);
    setEditForm({ ...user, password: '' });
    setShowEditPassword(false);
  };

  const saveEdit = async (e) => {
    e.preventDefault();
    if (editForm.password && editForm.password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    try {
      await api.put(`/users/${editForm.id}`, {
        full_name: editForm.full_name,
        email: editForm.email,
        role: editForm.role,
        is_active: editForm.is_active,
        ...(editForm.password ? { password: editForm.password } : {}),
      });
      toast.success('User updated');
      setEditUser(null);
      loadUsers();
    } catch (err) {
      toast.error(err.message || 'Failed to update user');
    }
  };

  const confirmDelete = async () => {
    try {
      await api.delete(`/users/${deleteUser.id}`);
      toast.success('User deleted');
      setDeleteUser(null);
      loadUsers();
    } catch (err) {
      toast.error(err.message || 'Failed to delete user');
    }
  };

  const RoleIcon = ({ role }) => {
    if (role === 'admin') return <ShieldCheck className="h-3.5 w-3.5" />;
    if (role === 'pharmacist') return <Shield className="h-3.5 w-3.5" />;
    return <UserCheck className="h-3.5 w-3.5" />;
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
          <input type="text" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search users..." className="w-full rounded-lg border border-gray-300 bg-white py-2.5 pl-10 pr-24 text-sm text-gray-900 placeholder-gray-400 transition-colors focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/20" />
          <button className="absolute right-1.5 top-1/2 -translate-y-1/2 rounded-md bg-gray-100 px-4 py-1.5 text-xs font-semibold text-gray-700 transition-colors hover:bg-gray-200">Find</button>
        </div>
        <button onClick={openAdd} className="flex items-center gap-2 rounded-lg bg-emerald-500 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition-all hover:bg-emerald-600 active:scale-[0.97]">
          <Plus className="h-4 w-4" />New User
        </button>
      </div>

      <div className="rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden">
        <div className="flex items-center justify-between border-b border-gray-100 px-6 py-4">
          <div className="flex items-center gap-2">
            <UserCog className="h-5 w-5 text-emerald-500" />
            <h2 className="text-base font-semibold text-gray-900">System Users</h2>
          </div>
          <div className="flex items-center gap-2">
            <span className="rounded-full bg-emerald-100 px-2.5 py-1 text-[11px] font-bold text-emerald-700">{activeCount} Active</span>
            <span className="rounded-full bg-gray-100 px-2.5 py-1 text-[11px] font-bold text-gray-600">{users.length - activeCount} Inactive</span>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="border-b border-gray-100 bg-gray-50/80">
                <th className="sticky-th-left px-6 py-3 whitespace-nowrap text-left text-[10px] font-bold uppercase tracking-wider text-gray-500">User</th>
                <th className="px-6 py-3 whitespace-nowrap text-left text-[10px] font-bold uppercase tracking-wider text-gray-500">Username</th>
                <th className="px-6 py-3 whitespace-nowrap text-left text-[10px] font-bold uppercase tracking-wider text-gray-500">Email</th>
                <th className="px-6 py-3 whitespace-nowrap text-left text-[10px] font-bold uppercase tracking-wider text-gray-500">Role</th>
                <th className="px-6 py-3 whitespace-nowrap text-left text-[10px] font-bold uppercase tracking-wider text-gray-500">Status</th>
                <th className="sticky-th-right px-6 py-3 whitespace-nowrap text-right text-[10px] font-bold uppercase tracking-wider text-gray-500">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filtered.map((user, idx) => (
                <tr key={user.id} className="group transition-colors hover:bg-gray-50/50">
                  <td className="sticky-col-left px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center gap-3">
                      <div className={clsx('flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full text-sm font-bold', avatarColors[idx % avatarColors.length])}>
                        {user.full_name.charAt(0)}
                      </div>
                      <span className="text-sm font-semibold text-gray-900">{user.full_name}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap font-mono text-sm text-gray-600">{user.username}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">{user.email}</td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={clsx('inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-semibold capitalize', roleColors[user.role] || 'bg-gray-100 text-gray-700')}>
                      <RoleIcon role={user.role} />
                      {user.role}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={clsx('inline-flex rounded-full px-2.5 py-1 text-[11px] font-semibold', user.is_active ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-500')}>
                      {user.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="sticky-col-right px-6 py-4 whitespace-nowrap text-right">
                    <div className="flex items-center justify-end gap-1.5">
                      <button onClick={() => setViewUser(user)} className="rounded-lg p-2 text-gray-400 transition-colors hover:bg-blue-50 hover:text-blue-600"><Eye className="h-4 w-4" /></button>
                      <button onClick={() => openEdit(user)} className="rounded-lg p-2 text-gray-400 transition-colors hover:bg-amber-50 hover:text-amber-600"><Edit3 className="h-4 w-4" /></button>
                      <button onClick={() => setDeleteUser(user)} className="rounded-lg p-2 text-gray-400 transition-colors hover:bg-red-50 hover:text-red-600"><Trash2 className="h-4 w-4" /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {filtered.length === 0 && (
          <div className="flex flex-col items-center justify-center py-16 text-gray-400">
            <UserCog className="mb-3 h-12 w-12 text-gray-300" />
            <p className="text-sm">No users found</p>
          </div>
        )}
      </div>

      {/* VIEW MODAL */}
      {viewUser &&
        createPortal(
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
            <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setViewUser(null)} />
            <div className="relative w-full max-w-lg rounded-2xl border border-gray-200 bg-white p-6 shadow-2xl">
              <div className="mb-4 flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900">User Details</h3>
                <button onClick={() => setViewUser(null)} className="rounded-lg p-2 text-gray-400 hover:bg-gray-100"><X className="h-5 w-5" /></button>
              </div>
              <div className="space-y-3 text-sm">
                <div className="flex justify-between border-b border-gray-100 pb-2"><span className="text-gray-500">Full Name</span><span className="font-semibold text-gray-900">{viewUser.full_name}</span></div>
                <div className="flex justify-between border-b border-gray-100 pb-2"><span className="text-gray-500">Username</span><span className="font-mono text-gray-900">{viewUser.username}</span></div>
                <div className="flex justify-between border-b border-gray-100 pb-2"><span className="text-gray-500">Email</span><span className="text-gray-900">{viewUser.email}</span></div>
                <div className="flex justify-between border-b border-gray-100 pb-2"><span className="text-gray-500">Role</span><span className={clsx('font-semibold capitalize', viewUser.role === 'admin' ? 'text-red-600' : viewUser.role === 'pharmacist' ? 'text-emerald-600' : 'text-blue-600')}>{viewUser.role}</span></div>
                <div className="flex justify-between"><span className="text-gray-500">Status</span><span className={clsx('font-semibold', viewUser.is_active ? 'text-emerald-600' : 'text-gray-500')}>{viewUser.is_active ? 'Active' : 'Inactive'}</span></div>
              </div>
            </div>
          </div>,
          document.body
        )}

      {/* ADD MODAL */}
      {showAdd &&
        createPortal(
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
            <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setShowAdd(false)} />
            <div className="relative w-full max-w-md rounded-2xl border border-gray-200 bg-white shadow-2xl">
              <div className="flex items-center justify-between border-b border-gray-100 px-6 py-4">
                <h3 className="text-lg font-semibold text-gray-900">Add New User</h3>
                <button onClick={() => setShowAdd(false)} className="rounded-lg p-2 text-gray-400 hover:bg-gray-100"><X className="h-5 w-5" /></button>
              </div>
              <form onSubmit={saveNew} className="px-6 py-5">
                <div className="space-y-4">
                  <div><label className="mb-1 block text-[11px] font-bold uppercase tracking-wider text-gray-500">Full Name *</label><input value={addForm.full_name} onChange={(e) => setAddForm({ ...addForm, full_name: e.target.value })} className="w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2.5 text-sm focus:border-emerald-500 focus:outline-none" required /></div>
                  <div><label className="mb-1 block text-[11px] font-bold uppercase tracking-wider text-gray-500">Username *</label><input value={addForm.username} onChange={(e) => setAddForm({ ...addForm, username: e.target.value })} className="w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2.5 text-sm focus:border-emerald-500 focus:outline-none" required /></div>
                  <div><label className="mb-1 block text-[11px] font-bold uppercase tracking-wider text-gray-500">Email *</label><input type="email" value={addForm.email} onChange={(e) => setAddForm({ ...addForm, email: e.target.value })} className="w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2.5 text-sm focus:border-emerald-500 focus:outline-none" required /></div>
                  <div>
                    <label className="mb-1 block text-[11px] font-bold uppercase tracking-wider text-gray-500">Password *</label>
                    <div className="relative">
                      <input
                        type={showAddPassword ? 'text' : 'password'}
                        value={addForm.password}
                        onChange={(e) => setAddForm({ ...addForm, password: e.target.value })}
                        minLength={6}
                        placeholder="At least 6 characters"
                        className="w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2.5 pr-11 text-sm focus:border-emerald-500 focus:outline-none"
                        required
                      />
                      <button
                        type="button"
                        onClick={() => setShowAddPassword((visible) => !visible)}
                        className="absolute inset-y-0 right-0 flex w-10 items-center justify-center text-gray-400 hover:text-gray-700"
                        aria-label={showAddPassword ? 'Hide password' : 'Show password'}
                        title={showAddPassword ? 'Hide password' : 'Show password'}
                      >
                        {showAddPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </div>
                  </div>
                  <div>
                    <label className="mb-1 block text-[11px] font-bold uppercase tracking-wider text-gray-500">Role</label>
                    <SearchableSelect
                      value={addForm.role}
                      onChange={(val) => setAddForm({ ...addForm, role: val })}
                      options={[
                        { value: 'cashier', label: 'Cashier' },
                        { value: 'pharmacist', label: 'Pharmacist' },
                        { value: 'admin', label: 'Admin' },
                      ]}
                      placeholder="Select Role"
                      searchPlaceholder="Search role..."
                    />
                  </div>
                </div>
                <div className="mt-5 flex justify-end gap-3 border-t border-gray-100 pt-5">
                  <button type="button" onClick={() => setShowAdd(false)} className="rounded-lg border border-gray-300 px-4 py-2.5 text-sm font-semibold text-gray-700 hover:bg-gray-50">Cancel</button>
                  <button type="submit" className="flex items-center gap-2 rounded-lg bg-emerald-500 px-5 py-2.5 text-sm font-semibold text-white hover:bg-emerald-600"><Save className="h-4 w-4" />Save User</button>
                </div>
              </form>
            </div>
          </div>,
          document.body
        )}

      {/* EDIT MODAL */}
      {editUser &&
        createPortal(
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
            <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setEditUser(null)} />
            <div className="relative w-full max-w-md rounded-2xl border border-gray-200 bg-white shadow-2xl">
              <div className="flex items-center justify-between border-b border-gray-100 px-6 py-4">
                <h3 className="text-lg font-semibold text-gray-900">Edit User</h3>
                <button onClick={() => setEditUser(null)} className="rounded-lg p-2 text-gray-400 hover:bg-gray-100"><X className="h-5 w-5" /></button>
              </div>
              <form onSubmit={saveEdit} className="px-6 py-5">
                <div className="space-y-4">
                  <div><label className="mb-1 block text-[11px] font-bold uppercase tracking-wider text-gray-500">Full Name</label><input value={editForm.full_name} onChange={(e) => setEditForm({ ...editForm, full_name: e.target.value })} className="w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2.5 text-sm focus:border-emerald-500 focus:outline-none" /></div>
                  <div><label className="mb-1 block text-[11px] font-bold uppercase tracking-wider text-gray-500">Email</label><input type="email" value={editForm.email} onChange={(e) => setEditForm({ ...editForm, email: e.target.value })} className="w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2.5 text-sm focus:border-emerald-500 focus:outline-none" /></div>
                  <div>
                    <label className="mb-1 block text-[11px] font-bold uppercase tracking-wider text-gray-500">New Password</label>
                    <div className="relative">
                      <input
                        type={showEditPassword ? 'text' : 'password'}
                        value={editForm.password || ''}
                        onChange={(e) => setEditForm({ ...editForm, password: e.target.value })}
                        minLength={6}
                        placeholder="Leave blank to keep current password"
                        className="w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2.5 pr-11 text-sm focus:border-emerald-500 focus:outline-none"
                      />
                      <button
                        type="button"
                        onClick={() => setShowEditPassword((visible) => !visible)}
                        className="absolute inset-y-0 right-0 flex w-10 items-center justify-center text-gray-400 hover:text-gray-700"
                        aria-label={showEditPassword ? 'Hide password' : 'Show password'}
                        title={showEditPassword ? 'Hide password' : 'Show password'}
                      >
                        {showEditPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </div>
                    <p className="mt-1 text-[11px] text-gray-500">Leave blank to keep the current password.</p>
                  </div>
                  <div>
                    <label className="mb-1 block text-[11px] font-bold uppercase tracking-wider text-gray-500">Role</label>
                    <SearchableSelect
                      value={editForm.role}
                      onChange={(val) => setEditForm({ ...editForm, role: val })}
                      options={[
                        { value: 'cashier', label: 'Cashier' },
                        { value: 'pharmacist', label: 'Pharmacist' },
                        { value: 'admin', label: 'Admin' },
                      ]}
                      placeholder="Select Role"
                      searchPlaceholder="Search role..."
                    />
                  </div>
                  <div>
                    <label className="mb-1 block text-[11px] font-bold uppercase tracking-wider text-gray-500">Status</label>
                    <SearchableSelect
                      value={editForm.is_active}
                      onChange={(val) => setEditForm({ ...editForm, is_active: parseInt(val) })}
                      options={[
                        { value: 1, label: 'Active' },
                        { value: 0, label: 'Inactive' },
                      ]}
                      placeholder="Select Status"
                      searchPlaceholder="Search status..."
                    />
                  </div>
                </div>
                <div className="mt-5 flex justify-end gap-3 border-t border-gray-100 pt-5">
                  <button type="button" onClick={() => setEditUser(null)} className="rounded-lg border border-gray-300 px-4 py-2.5 text-sm font-semibold text-gray-700 hover:bg-gray-50">Cancel</button>
                  <button type="submit" className="flex items-center gap-2 rounded-lg bg-emerald-500 px-5 py-2.5 text-sm font-semibold text-white hover:bg-emerald-600"><Save className="h-4 w-4" />Save Changes</button>
                </div>
              </form>
            </div>
          </div>,
          document.body
        )}

      {/* DELETE CONFIRM MODAL */}
      {deleteUser &&
        createPortal(
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
            <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setDeleteUser(null)} />
            <div className="relative w-full max-w-sm rounded-2xl border border-gray-200 bg-white p-6 shadow-2xl text-center">
              <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-red-100">
                <AlertTriangle className="h-7 w-7 text-red-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">Delete User?</h3>
              <p className="mt-2 text-sm text-gray-500">Are you sure you want to delete <strong>{deleteUser.full_name}</strong>?</p>
              <div className="mt-6 flex justify-center gap-3">
                <button onClick={() => setDeleteUser(null)} className="rounded-lg border border-gray-300 px-5 py-2.5 text-sm font-semibold text-gray-700 hover:bg-gray-50">Cancel</button>
                <button onClick={confirmDelete} className="rounded-lg bg-red-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-red-700">Delete</button>
              </div>
            </div>
          </div>,
          document.body
        )}
    </div>
  );
}
